export interface HslColor {
  h: number;
  s: number;
  l: number;
}

export function normalizeHex(value: string): string | null {
  const trimmed = value.trim();
  const hex6 = trimmed.match(/^#([0-9A-Fa-f]{6})$/);
  if (hex6) return `#${hex6[1].toLowerCase()}`;
  const hex3 = trimmed.match(/^#([0-9A-Fa-f]{3})$/);
  if (!hex3) return null;
  return `#${hex3[1]
    .split('')
    .map(ch => ch + ch)
    .join('')
    .toLowerCase()}`;
}

export function hexToHsl(hex: string): HslColor {
  const normalized = normalizeHex(hex) ?? '#000000';
  const r = parseInt(normalized.slice(1, 3), 16) / 255;
  const g = parseInt(normalized.slice(3, 5), 16) / 255;
  const b = parseInt(normalized.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const delta = max - min;

  if (delta === 0) {
    return { h: 0, s: 0, l: l * 100 };
  }

  const s = delta / (1 - Math.abs(2 * l - 1));
  let h = 0;
  if (max === r) h = ((g - b) / delta) % 6;
  else if (max === g) h = (b - r) / delta + 2;
  else h = (r - g) / delta + 4;
  h *= 60;
  if (h < 0) h += 360;

  return { h, s: s * 100, l: l * 100 };
}

export function hslToHex({ h, s, l }: HslColor): string {
  const sl = s / 100;
  const ll = l / 100;
  const c = (1 - Math.abs(2 * ll - 1)) * sl;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = ll - c / 2;
  let r = 0;
  let g = 0;
  let b = 0;

  if (h < 60) {
    r = c;
    g = x;
  } else if (h < 120) {
    r = x;
    g = c;
  } else if (h < 180) {
    g = c;
    b = x;
  } else if (h < 240) {
    g = x;
    b = c;
  } else if (h < 300) {
    r = x;
    b = c;
  } else {
    r = c;
    b = x;
  }

  const toHex = (channel: number) =>
    Math.round((channel + m) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Black/white/gray have no visible hue. If the user moves the hue slider,
 * lift saturation and lightness so the hex actually changes.
 */
export function hslFromPicker(prev: HslColor, next: HslColor): HslColor {
  const hueMoved = Math.abs(next.h - prev.h) > 0.5;
  const wasAchromatic = prev.s <= 1 || prev.l <= 1 || prev.l >= 99;
  if (hueMoved && wasAchromatic) {
    return { h: next.h, s: 100, l: 50 };
  }
  return next;
}
